import React, { FC, useEffect, useState } from 'react';
import styles from './target.module.scss';
import { useParentOrganizationContext } from 'context/ParentOrganisationContext';
import DashboardContainer from 'views/Dashboard/DashboardContainer';
import { DashboardContainerColorProps } from 'enums/DashboardContainerEnums';
import EmptyStateComponent from 'shared/components/EmptyStateComponent';
import { scopeTextFormatter, TargetTexts } from './constants';
import { CompanyDashboardProps } from 'views/CompanyDashboard/constants';
import MyLineChart from 'views/CompanyDashboard/LineChart';
import ParentCompanySetupService from 'services/ParentCompanySetup/parentCompanySetup.service';
import Header from './Header';
import { ReactComponent as EditIcon } from 'assets/icons/draft.svg';
import { Col, Row, Select, Spin } from 'antd';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import clsx from 'clsx';
import Details from './detailsSection';
import { Target } from 'models/Target/Target.model';
import DeleteTargetModal from './DeleteTargetModal';
import SbtiService from 'services/Sbti/sbti.service';
import { SbtiTarget } from 'models/Sbti/sbti.model';
import TargetService from 'services/TargetService/target.service';
import ProcessingFacilityModal from 'shared/components/Products/ProcessingFacilityModal';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { truncateWithEllipsis } from 'shared/utils/truncateWithElipsis';
import { ReactComponent as Arrow } from 'assets/icons/arrow.svg';

const { Option } = Select;

const TargetPage: FC = () => {
  const { organizationData } = useParentOrganizationContext();
  const { fetchParentOrganization } = ParentCompanySetupService();
  const { fetchSbtiTargets, sbtiTargets, loading } = SbtiService();
  const {
    loading: targetLoading,
    parentOrgLoading,
    isCustomButtonLoading,
    getParentOrganization,
    organization,
  } = TargetService();
  const targets = sbtiTargets?.sbtiTarget || [];
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedTarget, setSelectedTarget] = useState<string>('');
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [selectedTargetId, setSelectedTargetId] = useState<number>();
  const [visible, setVisible] = useState(false);
  const [isCustomTarget, setIsCustomTarget] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isEditFlow, setIsEditFlow] = useState(false);

  useEffect(() => {
    fetchParentOrganization();
    fetchSbtiTargets();
  }, []);
  useEffect(() => {
    if (isEditFlow) {
      setIsEditFlow(false);
      return;
    }
    if (targets && targets.length > 0) {
      setSelectedTarget(targets[0].targetName || '');
      setSelectedTargetId(targets[0].id);
      setSelectedIndex(0);
    } else {
      setSelectedTarget('');
    }
  }, [targets]);

  const handleEmptyState = async () => {
    if (organizationData?.organization) {
      const response = await getParentOrganization({ isEmptyState: true });
      if (response?.name) {
        setVisible(true);
        setIsCustomTarget(false);
        setIsEdit?.(false);
      }
    } else {
      handleOpen();
    }
  };

  const onEditClick = (target?: Target) => {
    setSelectedTarget(String(target?.targetName));
    setVisible(true);
    if (target?.targetCategory === TargetTexts.SCIENCE_BASED) {
      setIsCustomTarget(false);
    } else {
      setIsCustomTarget(true);
    }
    setIsEdit(true);
    setIsEditFlow(true);
    setSelectedTargetId(target?.id);
  };

  const onDeleteClick = (targetId?: number) => {
    setIsDeleteModalOpen(true);
    setSelectedTargetId(targetId);
  };
  const handleOpen = () => setIsModalOpen(true);

  const handleClose = () => setIsModalOpen(false);

  const handleChange = (value: string) => {
    // Find the index of the selected target
    const index = targets.findIndex(
      (target: SbtiTarget) => target.id === Number(value)
    );
    const selected = targets[index];
    setSelectedTarget(selected?.targetName || '');
    setSelectedIndex(index !== -1 ? index : 0);
    setSelectedTargetId(selected?.id);
  };
  const validIndex =
    selectedIndex >= 0 && selectedIndex < targets.length ? selectedIndex : 0;
  const lineChartData = targets[validIndex]?.yoyEmission || [];
  const maxSbtiCO2e =
    sbtiTargets?.sbtiTarget?.reduce((max, entry) => {
      const maxInYoyEmission =
        entry?.yoyEmission?.reduce((maxYoy, yoyEntry) => {
          return Math.max(maxYoy, yoyEntry?.totalCO2e || 0);
        }, 0) || 0;

      return Math.max(max, maxInYoyEmission);
    }, 0) || 0;

  const getPopupContainer = (trigger: HTMLElement): HTMLElement => {
    return (trigger.parentNode as HTMLElement) || trigger;
  };
  return (
    <DashboardContainer bgColor={DashboardContainerColorProps.GREY_LIGHT}>
      <div className={styles.container}>
        <div className={styles.targetContainer}>
          <Header
            selectedTargetId={selectedTargetId}
            visible={visible}
            setVisible={setVisible}
            setIsCustomTarget={setIsCustomTarget}
            isCustomTarget={isCustomTarget}
            sbtiTargets={sbtiTargets?.sbtiTarget}
            fetchSbtiTargets={fetchSbtiTargets}
            isEdit={isEdit}
            setIsEdit={setIsEdit}
            getParentOrganization={getParentOrganization}
            organization={organization}
            parentOrgLoading={parentOrgLoading}
            isCustomButtonLoading={isCustomButtonLoading}
            setSelectedTarget={setSelectedTarget}
            setSelectedTargetId={setSelectedTargetId}
            setSelectedIndex={setSelectedIndex}
          />
        </div>
        {loading ? (
          <div className={styles.loader}>
            <Spin />
          </div>
        ) : (
          <>
            {targets && targets.length === 0 && (
              <div className={styles.sbtiTargetArea}>
                {sbtiTargets?.message}
                <div className={styles.subHeading}>
                  {TargetTexts.SBTI_TARGET}
                </div>
                <div className={styles.emptyState}>
                  {targetLoading ? (
                    <div className={styles.loader}>
                      <Spin />
                    </div>
                  ) : (
                    <div onClick={handleEmptyState}>
                      <EmptyStateComponent
                        title={TargetTexts.EMPTY_STATE_TITLE}
                        subTitle={TargetTexts.EMPTY_STATE_SUBTITLE}
                      />
                    </div>
                  )}
                </div>
                <ProcessingFacilityModal
                  modalOpen={isModalOpen}
                  handleClose={handleClose}
                  contentText={TargetTexts.NO_ORG_CREATED}
                />
              </div>
            )}
            {targets && targets.length > 0 && (
              <div className={styles.sbtiTargetArea}>
                {sbtiTargets?.message ? (
                  <Row align="middle" className={styles.message}>
                    <Col className={styles.messageInfoIcon}>
                      <ExclamationCircleFilled />
                    </Col>
                    <Col span={22}>{sbtiTargets?.message}</Col>
                  </Row>
                ) : null}
                <div className={styles.rowTargetArea}>
                  <div className={styles.subHeading}>
                    {TargetTexts.SBTI_TARGET}
                  </div>
                  <div className={styles.dropDownarea}>
                    <Select
                      className={styles.overlay}
                      value={truncateWithEllipsis(selectedTarget)}
                      getPopupContainer={getPopupContainer}
                      listHeight={120}
                      suffixIcon={<Arrow fontSize={44} />}
                      onChange={handleChange}>
                      {targets?.map((target: SbtiTarget | undefined) =>
                        target ? (
                          <Option key={target.id} value={target.id}>
                            <div className={styles.optionContainer}>
                              {truncateWithEllipsis(String(target?.targetName))}

                              {target?.targetCategory ===
                                TargetTexts.CUSTOM && (
                                <div className={styles.customLabel}>
                                  {TargetTexts.TAG}
                                </div>
                              )}

                              <div className={styles.iconArea}>
                                <div className={styles.icon}>
                                  <EditIcon
                                    onClick={() => onEditClick(target)}
                                  />
                                </div>
                                <div className={clsx(styles.deleteIcon)}>
                                  <DeleteIcon
                                    onClick={() => onDeleteClick(target.id)}
                                  />
                                </div>
                              </div>
                            </div>
                          </Option>
                        ) : null
                      )}
                    </Select>
                  </div>
                </div>
                <div className={styles.yoyEmissionChart}>
                  <div className={styles.cardContainer}>
                    <div className={styles.breakdownChart}>
                      <MyLineChart
                        height={390}
                        xPos
                        isTwoLine
                        maxSbtiValue={maxSbtiCO2e}
                        targetName={selectedTarget}
                        unit={CompanyDashboardProps.TCO2}
                        data={lineChartData}
                        targetId={selectedTargetId}
                        fetchYoYEmissions={fetchSbtiTargets}
                      />
                    </div>
                  </div>
                </div>
                <div className={styles.detailsSection}>
                  <div className={styles.detailsHeading}>
                    <div className={styles.subHeading}>
                      {targets?.[selectedIndex]?.targetName}
                    </div>
                    {targets?.[selectedIndex]?.targetCategory ===
                      TargetTexts.CUSTOM && (
                      <div className={styles.detailsTag}>{TargetTexts.TAG}</div>
                    )}
                  </div>
                  <div className={styles.possibleTarget}>
                    <div className={styles.key}>
                      {TargetTexts.POSSIBLE_TARGET_STATEMENT}
                    </div>
                    <div className={styles.name}>
                      {targets?.[selectedIndex]?.organizationName +
                        TargetTexts.COMMIT_TEXT +
                        scopeTextFormatter(
                          String(targets?.[selectedIndex]?.targetCoverage)
                        ) +
                        TargetTexts.EMISSION +
                        targets?.[selectedIndex]?.totalReduction?.toFixed(2) +
                        TargetTexts.BY +
                        targets?.[selectedIndex]?.targetYear +
                        TargetTexts.BASEYEAR +
                        targets?.[selectedIndex]?.baselineYear}
                    </div>
                  </div>
                </div>
                <Details data={sbtiTargets} index={selectedIndex} />
              </div>
            )}
          </>
        )}
        <DeleteTargetModal
          isModalOpen={isDeleteModalOpen}
          setIsModalOpen={setIsDeleteModalOpen}
          targetId={selectedTargetId}
          fetchSbtiTargets={fetchSbtiTargets}
        />
      </div>
    </DashboardContainer>
  );
};

export default TargetPage;
